import React, { useCallback, useEffect, useState } from "react";
import type { EntityCustomViewParams } from "@rebasepro/cms-types";
import { useSnackbarController } from "@rebasepro/app";
import { useSidePanel } from "@rebasepro/cms";
import {
    Alert,
    Button,
    Card,
    Chip,
    cls,
    defaultBorderMixin,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    ExternalLinkIcon,
    iconSize,
    LoadingButton,
    Select,
    SelectItem,
    SendIcon,
    TextField,
    Typography,
    Wand2Icon
} from "@rebasepro/ui";
import { client } from "../lib/rebaseClient";

/**
 * An outreach email, opened as an email.
 *
 * Replaces the field-by-field record form on the `emails` collection. A draft
 * opens as a composer — From, To, Subject, the message, what it was written
 * from, and Send — laid out like the composer in the working implementation
 * elsewhere that the inbox was ported from. A sent one opens as the message
 * that went out. The columns that are bookkeeping (order key, step, the
 * snapshot flag) are not on screen at all; they were never something to read.
 *
 * ── Edits are this view's own ────────────────────────────────────────
 *
 * The draft is held here and written straight to the row — Save, Send and
 * Reject all write it. It does NOT go through the form context. In the admin
 * this app runs, a `formView` Builder is rendered INSTEAD of the record form,
 * so what it is handed is the framework's read-only stand-in: `readOnly: true`
 * on every row, and `setFieldValue`, `save` and `formex.resetForm` all throw.
 * Built on that context, every draft was read-only, Send was hidden, and
 * choosing a mailbox reported an error after it had saved. Only `disabled` is
 * believed, which the stand-in leaves false and a view-only screen sets.
 *
 * ── Send is the approval ─────────────────────────────────────────────
 *
 * The send endpoint still refuses anything that is not `approved`, and still
 * runs every other gate (admin only, suppression re-check, daily cap, delivery
 * configured). What changed is where the approval happens: a person who has
 * read this message in full and pressed Send, then confirmed the recipient,
 * has approved it — more deliberately than dragging a card ever did. So Send
 * writes what is on screen with `approved`, and then asks the endpoint to
 * deliver it. The row can never reach `approved` without that click.
 */

type Values = Record<string, unknown>;

interface DeliveryMailbox {
    id: string;
    name: string;
    address: string;
    color: string;
    outreachDefault: boolean;
    /** Everything that stops a cold email going out from it, business rules included. */
    problems: string[];
}

interface Delivery {
    configured: boolean;
    problems: string[];
    sentToday: number;
    dailyCap: number;
    from: string | null;
    defaultMailboxId: string | null;
    mailboxes: DeliveryMailbox[];
}

// A type, not an interface: the data client wants a `Record<string, unknown>`,
// and only a type alias carries the implicit index signature that satisfies it.
type Person = {
    display_name?: string;
    handle?: string;
    profile_url?: string;
};

interface Redraft {
    subject: string;
    body: string;
    personalizationNotes: string;
    degraded: boolean;
}

type EnumOption = { id: string; label: string; color?: string };

const text = (value: unknown): string => (typeof value === "string" ? value : value == null ? "" : String(value));

/** Statuses where the words can still change. Sent mail is a record, not a draft. */
const EDITABLE = new Set(["needs_review", "approved", "failed"]);

function relationId(value: unknown): string | undefined {
    if (value == null || value === "") return undefined;
    if (typeof value === "object") {
        const id = (value as { id?: unknown }).id;
        return id == null ? undefined : String(id);
    }
    return String(value);
}

/** A function's refusal, in its own words — the send endpoint writes them for a person. */
function describe(err: unknown): string {
    const api = err as { body?: { error?: string; problems?: string[]; hint?: string }; message?: string };
    const problems = Array.isArray(api.body?.problems) && api.body!.problems!.length
        ? ` — ${api.body!.problems!.join("; ")}`
        : "";
    const hint = api.body?.hint ? ` ${api.body.hint}` : "";
    return `${api.body?.error ?? api.message ?? String(err)}${problems}${hint}`;
}

/**
 * Enough rows to show the whole message. The kit's multiline field is a plain
 * textarea with a fixed `rows`, and at a fixed twelve the opt-out line sat
 * below the fold — an email reviewed through a scrollbar is an email half-read.
 * Estimated at 80 characters a line, which overshoots a little at this width:
 * a spare blank line costs nothing, a hidden one is the bug.
 */
function rowsFor(body: string): number {
    const lines = body.split("\n").reduce((sum, line) => sum + Math.max(1, Math.ceil(line.length / 80)), 0);
    return Math.max(12, lines + 1);
}

function stamp(value: unknown): string {
    if (!value) return "";
    const date = new Date(text(value));
    return Number.isNaN(date.getTime()) ? "" : date.toLocaleString();
}

export function OutreachEmailView({ collection, entity, formContext }: EntityCustomViewParams) {
    const snackbar = useSnackbarController();
    const sidePanel = useSidePanel();

    const source = (formContext?.values ?? entity?.values) as Values | undefined;
    const [values, setValues] = useState<Values>(() => source ?? {});
    const [dirty, setDirty] = useState(false);
    useEffect(() => {
        // The row changed underneath — saved from elsewhere, or read again.
        // Follow it, unless somebody is mid-edit: their words win until saved.
        if (source && !dirty) setValues(source);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source]);
    useEffect(() => {
        if (!dirty) return;
        const warn = (event: BeforeUnloadEvent) => event.preventDefault();
        window.addEventListener("beforeunload", warn);
        return () => window.removeEventListener("beforeunload", warn);
    }, [dirty]);
    const id = entity?.id != null ? String(entity.id) : undefined;
    const status = text(values.status) || "needs_review";
    const prospectId = relationId(values.prospect);

    const statusEnum = ((collection.properties?.status as { enum?: EnumOption[] } | undefined)?.enum ?? []);
    const statusOption = statusEnum.find(option => option.id === status);

    const [delivery, setDelivery] = useState<Delivery | undefined>();
    const [mailboxId, setMailboxId] = useState<string | undefined>(relationId(values.mailbox));
    const [person, setPerson] = useState<Person | undefined>();
    const [instruction, setInstruction] = useState("");
    const [drafting, setDrafting] = useState(false);
    const [sending, setSending] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [error, setError] = useState<string | undefined>();

    useEffect(() => {
        let live = true;
        client.functions.invoke<Delivery>("outreach", undefined, { method: "GET", path: "/status" })
            .then(result => {
                if (!live) return;
                setDelivery(result);
                setMailboxId(current => current ?? result.defaultMailboxId ?? undefined);
            })
            .catch(err => { if (live) setError(`Could not read the delivery settings: ${describe(err)}`); });
        return () => { live = false; };
    }, []);

    useEffect(() => {
        if (!prospectId) { setPerson(undefined); return; }
        let live = true;
        client.data.collection<Person>("prospects").find({ where: { id: ["==", prospectId] } as never, limit: 1 })
            .then(({ data }) => { if (live) setPerson((data ?? [])[0]); })
            .catch(() => { /* the name is a nicety; the address below is what matters */ });
        return () => { live = false; };
    }, [prospectId]);

    const set = useCallback((key: string, value: unknown) => {
        setValues(current => ({ ...current, [key]: value }));
        setDirty(true);
    }, []);

    /** Write the row, with whatever is on screen, and call it saved. */
    const persist = useCallback(async (patch: Values) => {
        if (!id) throw new Error("This email has not been saved yet.");
        const next = { ...values, ...patch };
        await client.data.collection("emails").update(id, {
            ...(mailboxId ? { mailbox: mailboxId } : {}),
            to_email: text(next.to_email).trim(),
            subject: text(next.subject),
            body: text(next.body),
            personalization_notes: text(next.personalization_notes),
            degraded: Boolean(next.degraded),
            status: next.status
        });
        setValues(next);
        setDirty(false);
    }, [id, values, mailboxId]);

    const [saving, setSaving] = useState(false);
    const save = async () => {
        setSaving(true);
        setError(undefined);
        try {
            await persist({});
            snackbar.open({ type: "success", message: "Saved." });
        } catch (err) {
            setError(describe(err));
        } finally {
            setSaving(false);
        }
    };

    /**
     * Choosing where it goes out from is saved at once, not left for Send: the
     * choice is part of the draft, and a reviewer who picks the mailbox and
     * closes the tab should find it picked tomorrow.
     */
    const chooseMailbox = async (next: string) => {
        setMailboxId(next);
        if (!id) return;
        try {
            await client.data.collection("emails").update(id, { mailbox: next });
        } catch (err) {
            setError(describe(err));
        }
    };

    const redraft = async () => {
        if (!id) return;
        setDrafting(true);
        setError(undefined);
        try {
            const draft = await client.functions.invoke<Redraft>("outreach", {
                instruction,
                subject: text(values.subject),
                body: text(values.body)
            }, { method: "POST", path: `/${id}/redraft` });
            // A templated fallback is worse than what is on screen, and swapping
            // it in would throw away whatever the reviewer already fixed.
            if (draft.degraded) {
                setError(`No model could write it, so nothing was changed. ${draft.personalizationNotes}`);
                return;
            }
            set("subject", draft.subject);
            set("body", draft.body);
            set("personalization_notes", draft.personalizationNotes);
            set("degraded", false);
            setInstruction("");
        } catch (err) {
            setError(describe(err));
        } finally {
            setDrafting(false);
        }
    };

    const send = async () => {
        if (!id) return;
        setSending(true);
        setError(undefined);
        const to = text(values.to_email).trim();
        try {
            await persist({ status: "approved" });
            const result = await client.functions.invoke<{ email?: Values }>("outreach", undefined, {
                method: "POST",
                path: `/${id}/send`
            });
            const stored = result?.email ?? {};
            setValues(current => ({
                ...current,
                status: "sent",
                sent_at: stored.sent_at ?? new Date().toISOString(),
                approved_by: stored.approved_by ?? current.approved_by,
                error: null
            }));
            setDirty(false);
            snackbar.open({ type: "success", message: `Sent to ${to}.` });
        } catch (err) {
            // The words stay exactly as they are on screen. A delivery failure
            // is recorded on the row by the endpoint; the reason is shown here.
            setError(describe(err));
        } finally {
            setSending(false);
            setConfirming(false);
        }
    };

    const reject = async () => {
        setError(undefined);
        try {
            await persist({ status: "rejected" });
            snackbar.open({ type: "info", message: "Rejected. The person stays in People — nothing is deleted." });
        } catch (err) {
            setError(describe(err));
        }
    };

    const restore = async () => {
        setError(undefined);
        try {
            await persist({ status: "needs_review" });
        } catch (err) {
            setError(describe(err));
        }
    };

    const to = text(values.to_email).trim();
    const subject = text(values.subject);
    const body = text(values.body);
    const notes = text(values.personalization_notes);
    // `readOnly` is deliberately NOT read — see the note at the top.
    const editable = EDITABLE.has(status) && !formContext?.disabled;
    const busy = drafting || sending || saving;
    const chosen = delivery?.mailboxes.find(m => m.id === mailboxId);
    const sendProblems = !delivery ? [] : chosen ? chosen.problems : delivery.problems;
    const canSend = Boolean(delivery) && Boolean(chosen) && sendProblems.length === 0
        && to.includes("@") && subject.trim() !== "" && body.trim() !== "";
    const capReached = delivery ? delivery.sentToday >= delivery.dailyCap : false;

    const header = (
        <div className="flex items-center gap-2 flex-wrap">
            <Chip size="small" colorScheme={`${statusOption?.color ?? "gray"}Lighter` as never}>
                {statusOption?.label ?? status}
            </Chip>
            {Boolean(values.degraded) && <Chip size="small" colorScheme="orangeLighter">Templated — no model wrote this</Chip>}
            {prospectId && (
                <Button
                    size="small"
                    variant="text"
                    onClick={() => sidePanel.open({ path: "prospects", entityId: prospectId })}
                >
                    {person?.display_name || person?.handle || "Open the person"}
                </Button>
            )}
            {person?.profile_url && (
                <Button
                    size="small"
                    variant="text"
                    component="a"
                    href={person.profile_url}
                    target="_blank"
                    rel="noreferrer"
                >
                    <ExternalLinkIcon size={iconSize.smallest} />
                    Where they said it
                </Button>
            )}
        </div>
    );

    // ── A message that has gone out: read it, do not edit it ──
    if (!EDITABLE.has(status) && status !== "rejected") {
        return (
            <div className="w-full max-w-3xl mx-auto flex flex-col gap-4 pt-6 pb-16 px-5 sm:px-8">
                {header}
                <Card className={cls("p-4 flex flex-col gap-3", defaultBorderMixin)}>
                    <div className="flex flex-col">
                        <Typography variant="body2" className="font-medium truncate">
                            {chosen?.address ?? delivery?.from ?? "Us"}
                        </Typography>
                        <Typography variant="body2" color="secondary" className="truncate">
                            {stamp(values.sent_at)}
                            {to && ` · to ${to}`}
                            {text(values.approved_by) && ` · sent by ${text(values.approved_by)}`}
                        </Typography>
                    </div>
                    <Typography variant="subtitle1">{subject || "(no subject)"}</Typography>
                    <Typography variant="body1" className="whitespace-pre-wrap break-words">{body}</Typography>
                    <Typography variant="caption" color="secondary">
                        The unsubscribe link and postal address went out below this text.
                    </Typography>
                </Card>
            </div>
        );
    }

    return (
        <div className="w-full max-w-3xl mx-auto flex flex-col gap-4 pt-6 pb-16 px-5 sm:px-8">
            {header}

            <Card className={cls("p-4 flex flex-col gap-3", defaultBorderMixin)}>
                {status === "rejected" && (
                    <Alert
                        color="base"
                        size="small"
                        action={<Button size="small" variant="text" onClick={() => void restore()}>Back to review</Button>}
                    >
                        Rejected — this will not be sent.
                    </Alert>
                )}

                {delivery && sendProblems.length > 0 && (
                    <Alert color="warning" size="small">
                        This email cannot go out {chosen ? `from ${chosen.address} ` : ""}yet. You can still edit and save it.
                        Missing: {sendProblems.join("; ")}.
                    </Alert>
                )}

                {status === "failed" && text(values.error) && (
                    <Alert color="error" size="small">
                        The last attempt did not send: {text(values.error)}
                    </Alert>
                )}

                <div className="flex gap-2 flex-wrap">
                    <div className="grow min-w-[14rem]">
                        <Select
                            size="small"
                            label="From"
                            fullWidth
                            value={mailboxId ?? ""}
                            disabled={!editable || busy || !delivery?.mailboxes.length}
                            onValueChange={value => void chooseMailbox(String(value))}
                            renderValue={value => delivery?.mailboxes.find(m => m.id === value)?.address ?? "Choose a mailbox"}
                        >
                            {(delivery?.mailboxes ?? []).map(m => (
                                <SelectItem key={m.id} value={m.id}>
                                    {m.address}{m.outreachDefault ? " — for cold outreach" : ""}{m.problems.length ? " (cannot send yet)" : ""}
                                </SelectItem>
                            ))}
                        </Select>
                    </div>
                    <TextField
                        size="small"
                        label="To"
                        type="email"
                        value={to}
                        disabled={busy}
                        readOnly={!editable}
                        className="grow"
                        onChange={event => set("to_email", event.target.value)}
                    />
                </div>

                <TextField
                    size="small"
                    label="Subject"
                    value={subject}
                    disabled={busy}
                    readOnly={!editable}
                    onChange={event => set("subject", event.target.value)}
                />

                {editable && (
                    <div className="flex gap-2 flex-wrap items-end">
                        <TextField
                            size="small"
                            label="What should change? (optional)"
                            placeholder="shorter, lead with the admin panel, less formal…"
                            value={instruction}
                            disabled={busy}
                            className="grow"
                            onChange={event => setInstruction(event.target.value)}
                        />
                        <Button size="small" variant="outlined" disabled={busy || !prospectId} onClick={() => void redraft()}>
                            <Wand2Icon size={iconSize.smallest} />
                            {drafting ? "Rewriting…" : "Rewrite with AI"}
                        </Button>
                    </div>
                )}

                <TextField
                    size="small"
                    label="Message"
                    multiline
                    minRows={rowsFor(body)}
                    value={body}
                    disabled={busy}
                    readOnly={!editable}
                    onChange={event => set("body", event.target.value)}
                />
                <Typography variant="caption" color="secondary">
                    An unsubscribe link and the postal address are added below this when it is sent.
                </Typography>

                {/*
                    What the draft was written from. Shown, not tucked away: the
                    model may only assert what it was given, so this is how a
                    sentence in the draft is checked as a fact or an invention.
                */}
                {notes && (
                    <div className="flex flex-col gap-1">
                        <Typography variant="body2" color="secondary" className="font-medium">
                            What the draft was written from
                        </Typography>
                        <Typography variant="body2" color="secondary" className="whitespace-pre-wrap break-words">
                            {notes}
                        </Typography>
                    </div>
                )}

                {error && <Alert color="error" size="small">{error}</Alert>}

                {editable && (
                    <div className="flex gap-2 items-center flex-wrap">
                        <Button
                            size="small"
                            variant="filled"
                            color="primary"
                            disabled={!canSend || capReached || busy}
                            onClick={() => setConfirming(true)}
                        >
                            <SendIcon size={iconSize.smallest} />
                            {sending ? "Sending…" : "Send"}
                        </Button>
                        <LoadingButton size="small" variant="outlined" loading={saving} disabled={!dirty || busy} onClick={() => void save()}>
                            {dirty ? "Save draft" : "Saved"}
                        </LoadingButton>
                        <Button size="small" variant="text" disabled={busy} onClick={() => void reject()}>
                            Reject
                        </Button>
                        {delivery?.configured && (
                            <Typography variant="caption" color="secondary" className="ml-auto">
                                {capReached
                                    ? `Today's cap of ${delivery.dailyCap} is reached.`
                                    : `${delivery.sentToday} of ${delivery.dailyCap} sent today.`}
                            </Typography>
                        )}
                    </div>
                )}
            </Card>

            {/* The one irreversible thing this app does, so the address is read once more. */}
            <Dialog open={confirming} onOpenChange={open => { if (!sending) setConfirming(open); }} maxWidth="md">
                <DialogTitle variant="h6">Send this email?</DialogTitle>
                <DialogContent className="flex flex-col gap-2">
                    <Typography variant="body1">
                        To <span className="font-medium">{to}</span>
                    </Typography>
                    <Typography variant="body2" color="secondary">“{subject}”</Typography>
                    <Typography variant="body2" color="secondary">
                        This delivers a real message to a real person and cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button variant="text" disabled={sending} onClick={() => setConfirming(false)}>Cancel</Button>
                    <LoadingButton variant="filled" color="primary" loading={sending} onClick={() => void send()}>
                        Send
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </div>
    );
}
